import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { DateTime } from "luxon";

import PerformanceCard from "./PerformanceCard";
import { ReactComponent as PerformanceCurrency } from "@/assets/icons/performance-currency.svg";
import { ReactComponent as PerformanceUser } from "@/assets/icons/performance-users.svg";
import { ReactComponent as PerformanceMedal } from "@/assets/icons/performance-medal.svg";
import { ReactComponent as PerformanceCtr } from "@/assets/icons/performance-ctr.svg";
import { ReactComponent as PerformanceRocket } from "@/assets/icons/performance-rocket.svg";
import { api } from "@/utils/api";
import { TestingCompetitionOverviewResource } from "@/types/resources";
import ContentSpinner from "@/components/ContentSpinner";
import { ActivePeriod } from "..";
import { Periods } from "@/pages/GamificationBonuses/components/GamificationDateFilter";

const Grid = styled.div`
  margin-bottom: 56px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px;
`;

interface Props {
  activePeriod: ActivePeriod;
  dateParams: {
    dateFrom: string;
    dateTo: string;
  } | null;
}

const PerformanceCards: FC<Props> = ({ activePeriod, dateParams }) => {
  const { data: overview, isLoading } =
    useQuery<TestingCompetitionOverviewResource>(
      ["leaderboard_overview", { period: activePeriod.period }],
      () =>
        api.get<never, TestingCompetitionOverviewResource>(
          "/v1/gamification/thumbnails/overview",
          {
            params: {
              ...(!!dateParams && {
                date_from: dateParams.dateFrom,
                date_to: dateParams.dateTo,
              }),
            },
          }
        ),
      {
        refetchOnMount: false,
        staleTime: 1000 * 120,
      }
    );

  const renderBadgeDateTitle = (activePeriod: ActivePeriod) => {
    if (activePeriod.type === "period") {
      switch (activePeriod.period) {
        case Periods.LAST_90_DAYS:
          return "Last 90 days";
        default:
          return "All time";
      }
    } else {
      return DateTime.fromISO(activePeriod.period).toFormat("yyyy LLLL");
    }
  };

  return (
    <>
      {!!isLoading ? (
        <ContentSpinner />
      ) : (
        <Grid>
          <PerformanceCard
            value={`${overview?.bonus?.sum ?? "-"} €`}
            title="Bonus"
            subtitle={`${
              overview?.bonus.competitionsCount ?? "-"
            } competitions won`}
            date={renderBadgeDateTitle(activePeriod)}
          >
            <PerformanceCurrency />
          </PerformanceCard>
          <PerformanceCard
            value={overview?.totalParticipants.count ?? "-"}
            title="Total Participants"
            subtitle="All Teams"
            date={renderBadgeDateTitle(activePeriod)}
            teamIds={overview?.totalParticipants?.teamIds}
            isTeamsCount
          >
            <PerformanceUser />
          </PerformanceCard>
          <PerformanceCard
            value={overview?.mostWins?.wins ?? "-"}
            title="Most Wins"
            subtitle={overview?.mostWins?.name || "-"}
            date={renderBadgeDateTitle(activePeriod)}
          >
            <PerformanceMedal />
          </PerformanceCard>
          <PerformanceCard
            value={`${overview?.highestCtr?.ctr ?? "-"} %`}
            title="Highest CTR"
            subtitle={overview?.highestCtr?.name || "-"}
            date={renderBadgeDateTitle(activePeriod)}
          >
            <PerformanceCtr />
          </PerformanceCard>
          <PerformanceCard
            value={overview?.openCompetitions?.count ?? 0}
            title="Open Competitions"
            subtitle="All Teams"
            teamIds={overview?.openCompetitions?.teamIds}
            isTeamsCount
          >
            <PerformanceRocket />
          </PerformanceCard>
        </Grid>
      )}
    </>
  );
};

export default PerformanceCards;
